// Сметките на справка „Отстъпки и надбавки".
//
// BE не праща отделно поле „това надбавка ли е" — знакът НА ПРОЦЕНТА е видът
// на корекцията (`discount_percent < 0` = надбавка, защото вдига цената).
// Разделянето става тук веднъж и всичко нататък чете `kind`, вместо всеки
// компонент да преповтаря сравнението с нула.
//
// Второ следствие от същата конвенция: `total_discount` е ОТРИЦАТЕЛЕН при
// надбавка. Затова редовият модел носи `adjustment` — знаковото отражение
// върху сметката (< 0 отстъпка, > 0 надбавка) — и таблицата сортира и сумира
// него, а не сурово поле, чийто знак значи обратното на очакваното.

import { parseDecimal } from "../../../../components/dataTable";
import type { DiscountReportItem } from "../../../../api/reportsDiscounts";

/** Отстъпка над този процент минава за „изисква одобрение". */
export const APPROVAL_PERCENT = 30;

export type DiscountKind = "discount" | "surcharge";

/**
 * Редът, както влиза в таблицата. Всички ЕТИКЕТИ (вид, сервитьор, клиент) са
 * разрешени тук, в данните — не в `accessorFn`: TanStack кешира стойността на
 * клетката за живота на `data`, а React Query пази същата референция при
 * непроменен отговор, тоест превод, сменен по-късно, никога не би стигнал до
 * клетката.
 */
export interface NamedDiscountRow extends DiscountReportItem {
  /** Стабилен ключ: една сметка може да носи повече от една корекция. */
  rowId: string;
  billLabel: string;
  waiterLabel: string;
  clientLabel: string;
  kind: DiscountKind;
  kindLabel: string;
  typeLabel: string;
  /** Процентът БЕЗ знак — посоката я носи колоната „Вид". */
  percentAbs: number;
  /** Знаковото отражение върху сметката: < 0 отстъпка, > 0 надбавка. */
  adjustment: number;
  amountBefore: number;
  paid: number;
}

export interface DiscountLabels {
  discount: string;
  surcharge: string;
  /** Превод на `discount_type` (fixed_percent, cashback…). */
  discountType: (type: string | null) => string;
}

/**
 * Името на реда в колоната „Сметка" — името на МАСАТА, както го вижда
 * сервитьорът. Номерът на сметката остава като резерва за отговор от по-старо
 * BE (полето е добавено в 0.0.224) и за служебна сметка без име.
 */
export const billLabelOf = (item: DiscountReportItem): string =>
  item.bill_name?.trim() || `#${item.bill_id}`;

export const buildDiscountRows = (
  items: readonly DiscountReportItem[],
  labels: DiscountLabels,
): NamedDiscountRow[] =>
  items.map((item, index) => {
    const percent = parseDecimal(item.discount_percent);
    const kind: DiscountKind = percent < 0 ? "surcharge" : "discount";
    return {
      ...item,
      rowId: `${item.bill_id}-${index}`,
      billLabel: billLabelOf(item),
      waiterLabel: item.waiter_name ?? "",
      clientLabel: item.client_name ?? "",
      kind,
      kindLabel: kind === "surcharge" ? labels.surcharge : labels.discount,
      typeLabel: labels.discountType(item.discount_type),
      percentAbs: Math.abs(percent),
      adjustment: -parseDecimal(item.total_discount),
      amountBefore: parseDecimal(item.total_amount),
      paid: parseDecimal(item.paid_amount),
    };
  });

export interface DiscountsSummary {
  /** Всички редове с корекция — отстъпки и надбавки заедно. */
  rows: number;
  discountBills: number;
  surchargeBills: number;
  /** Дадените отстъпки, ПОЛОЖИТЕЛНО число. */
  discountTotal: number;
  /** Добавените надбавки, ПОЛОЖИТЕЛНО число. */
  surchargeTotal: number;
  amountBefore: number;
  paid: number;
  /** Средна отстъпка на сметка с отстъпка. */
  averageDiscount: number;
  /** Сметки с отстъпка над `APPROVAL_PERCENT`. */
  aboveApproval: number;
}

const EMPTY_SUMMARY: DiscountsSummary = {
  rows: 0,
  discountBills: 0,
  surchargeBills: 0,
  discountTotal: 0,
  surchargeTotal: 0,
  amountBefore: 0,
  paid: 0,
  averageDiscount: 0,
  aboveApproval: 0,
};

/**
 * Обобщението за целия период. Работи върху СУРОВИЯ отговор, за да може
 * същата функция да смята и предходния период за Δ чиповете, без да се строи
 * редови модел с етикети, който никой няма да покаже.
 */
export const summarizeDiscounts = (
  items: readonly DiscountReportItem[],
): DiscountsSummary => {
  if (!items.length) return EMPTY_SUMMARY;

  const summary: DiscountsSummary = { ...EMPTY_SUMMARY, rows: items.length };

  for (const item of items) {
    const percent = parseDecimal(item.discount_percent);
    const adjustment = parseDecimal(item.total_discount);
    summary.amountBefore += parseDecimal(item.total_amount);
    summary.paid += parseDecimal(item.paid_amount);
    if (percent < 0) {
      summary.surchargeBills += 1;
      summary.surchargeTotal += Math.abs(adjustment);
      continue;
    }
    summary.discountBills += 1;
    summary.discountTotal += adjustment;
    if (percent > APPROVAL_PERCENT) summary.aboveApproval += 1;
  }

  summary.averageDiscount = summary.discountBills
    ? summary.discountTotal / summary.discountBills
    : 0;

  return summary;
};
